import { TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID } from '@/utils/tapeAplaColor';

export const CUSTOM_COLOR_OPTION_VALUE = 'custom-color';
export const CARTON_CUSTOM_EXTERNAL_COLOR_ATTRIBUTE_ID = 'cartons.customExternalColor';
export const CARTON_CUSTOM_INTERNAL_COLOR_ATTRIBUTE_ID = 'cartons.customInternalColor';
export const BAG_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID = 'bag.customMaterialColor';

export const CUSTOM_COLOR_FIELDS = [
	{ id: CARTON_CUSTOM_EXTERNAL_COLOR_ATTRIBUTE_ID, label: 'Kolor zewnętrzny' },
	{ id: CARTON_CUSTOM_INTERNAL_COLOR_ATTRIBUTE_ID, label: 'Kolor wewnętrzny' },
	{ id: BAG_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID, label: 'Kolor własny' },
	{ id: TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID, label: 'Kolor apli' },
] as const;

export const CUSTOM_COLOR_CONFIGURATION_KEYS: string[] = CUSTOM_COLOR_FIELDS.map(
	(field) => field.id
);

const CUSTOM_COLOR_KEY_SET = new Set(CUSTOM_COLOR_CONFIGURATION_KEYS);

const pickValue = (value: unknown) => (Array.isArray(value) ? value[0] : value);

const normalizeOptionText = (value: unknown) =>
	typeof value === 'string' || typeof value === 'number'
		? String(value)
				.trim()
				.toLowerCase()
				.normalize('NFD')
				.replace(/ł/g, 'l')
				.replace(/[\u0300-\u036f]/g, '')
				.replace(/[^a-z0-9]+/g, '')
		: '';

/**
 * Keep the hex the user typed/picked. Only add a missing `#`.
 * Do not uppercase, expand 3-digit values, or substitute defaults.
 */
export const formatUserEnteredHex = (value: unknown): string | null => {
	if (typeof value === 'number') return null;
	const raw = pickValue(value);
	if (typeof raw !== 'string') return null;
	const trimmed = raw.trim();
	if (!trimmed) return null;
	if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
		return trimmed.length === 9 ? trimmed.slice(0, 7) : trimmed;
	}
	if (/^([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed) && /[a-fA-F]/.test(trimmed)) {
		return `#${trimmed.length === 8 ? trimmed.slice(0, 6) : trimmed}`;
	}
	return null;
};

export const normalizeHexColor = formatUserEnteredHex;

export const isCustomColorChoice = (value: unknown, label?: unknown): boolean => {
	const candidates = [value, label].map(normalizeOptionText).filter(Boolean);
	return candidates.some(
		(candidate) =>
			candidate === 'customcolor' ||
			candidate === 'customcolour' ||
			candidate === 'wlasnykolor' ||
			candidate === 'wlasnykolormaterialu' ||
			candidate.includes('wlasnykolor')
	);
};

export const isCustomColorConfigurationKey = (key: string) => {
	const normalized = key.toLowerCase();
	if (CUSTOM_COLOR_KEY_SET.has(key)) return true;
	return normalized.includes('custom') && normalized.includes('color');
};

export type ColorSummaryRow = {
	id: string;
	label: string;
	value: string;
};

export const collectCustomColorRows = (
	configuration: Record<string, unknown> | null | undefined,
	fromDefinition: ColorSummaryRow[] = []
): ColorSummaryRow[] => {
	if (!configuration && !fromDefinition.length) return [];

	const rows: ColorSummaryRow[] = [];
	const usedKeys = new Set<string>();
	const usedLabels = new Set<string>();

	const push = (id: string, label: string, hex: string) => {
		const labelKey = label.toLowerCase();
		if (usedKeys.has(id) || usedLabels.has(labelKey)) return;
		usedKeys.add(id);
		usedLabels.add(labelKey);
		rows.push({ id, label, value: hex });
	};

	fromDefinition.forEach((row) => {
		const hex = formatUserEnteredHex(row.value);
		if (!hex) return;
		push(row.id, row.label, hex);
	});

	if (!configuration) return rows;

	CUSTOM_COLOR_FIELDS.forEach((field) => {
		const hex = formatUserEnteredHex(configuration[field.id]);
		if (!hex) return;
		push(field.id, field.label, hex);
	});

	return rows;
};

const relatedCustomColorHexes = (rowLabel: string, colorRows: ColorSummaryRow[]): string[] => {
	const label = normalizeOptionText(rowLabel);
	const isApla = label.includes('apl');
	const hexes: string[] = [];
	colorRows.forEach((colorRow) => {
		const colorIsApla = normalizeOptionText(colorRow.label).includes('apl');
		if (isApla !== colorIsApla) return;
		if (hexes.includes(colorRow.value)) return;
		hexes.push(colorRow.value);
	});
	return hexes;
};

export const mergeCustomColorHexRows = <T extends { id: string; label: string; value: string }>(
	rows: T[],
	configuration: Record<string, unknown> | null | undefined,
	fromDefinition: ColorSummaryRow[] = []
): T[] => {
	const colorRows = collectCustomColorRows(configuration, fromDefinition);
	if (!colorRows.length) return rows;

	const next = [...rows];

	next.forEach((row, index) => {
		if (!isCustomColorChoice(row.value, row.label)) return;
		if (formatUserEnteredHex(row.value)) return;
		if (/\(#[0-9A-Fa-f]{3,8}\)/.test(row.value)) return;
		const related = relatedCustomColorHexes(row.label, colorRows);
		if (!related.length) return;
		next[index] = { ...row, value: `${row.value} (${related.join(', ')})` };
	});

	colorRows.forEach((colorRow) => {
		const colorIsApla = normalizeOptionText(colorRow.label).includes('apl');
		const labelKey = colorRow.label.toLowerCase();
		const existingIndex = next.findIndex(
			(row) => row.id === colorRow.id || row.label.trim().toLowerCase() === labelKey
		);
		if (existingIndex >= 0) {
			const existing = next[existingIndex];
			if (
				!formatUserEnteredHex(existing.value) &&
				!existing.value.includes(colorRow.value)
			) {
				next[existingIndex] = {
					...existing,
					value: `${existing.value} (${colorRow.value})`.trim(),
				};
			}
			return;
		}

		const parentIndex = next.findIndex((row) => {
			if (!isCustomColorChoice(row.value, row.label)) return false;
			const rowIsApla = normalizeOptionText(row.label).includes('apl');
			return rowIsApla === colorIsApla;
		});
		const colorLabelKeys = new Set(colorRows.map((row) => row.label.toLowerCase()));
		let insertAt = parentIndex >= 0 ? parentIndex + 1 : next.length;
		if (parentIndex >= 0) {
			while (
				insertAt < next.length &&
				colorLabelKeys.has(next[insertAt].label.trim().toLowerCase())
			) {
				insertAt += 1;
			}
		}
		next.splice(insertAt, 0, { ...colorRow } as T);
	});

	return next;
};
